import { useLocation } from "@tanstack/react-router";
import { useTheme } from "./ThemeProvider";
import { Sun, Moon, Menu, X, ChevronLeft, ChevronRight, ChevronDown, Search } from "lucide-react";
import { SidebarBrandLogo } from "./SidebarBrandLogo";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  sidebarNavGroups,
  sidebarOverviewItem,
  itemMatchesFilter,
} from "./sidebarNavData";
import { SidebarNavRow } from "./SidebarNavRow";
import { SidebarProductPicker } from "./SidebarProductPicker";

interface SidebarProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

const initialOpenGroups = (): Record<string, boolean> =>
  Object.fromEntries(sidebarNavGroups.map((g) => [g.id, true]));

export function Sidebar({ isCollapsed, onToggleCollapse }: SidebarProps) {
  const { theme, toggleTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [navFilter, setNavFilter] = useState("");
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(initialOpenGroups);
  const location = useLocation();

  const isNavActive = useCallback(
    (to: string) => {
      if (to === "/") return location.pathname === "/";
      return location.pathname === to || location.pathname.startsWith(`${to}/`);
    },
    [location.pathname],
  );

  const filteredOverviewVisible = useMemo(
    () => itemMatchesFilter(sidebarOverviewItem, navFilter),
    [navFilter],
  );

  const filteredGroups = useMemo(() => {
    const q = navFilter.trim();
    return sidebarNavGroups
      .map((g) => ({
        ...g,
        items: g.items.filter((item) => itemMatchesFilter(item, q)),
      }))
      .filter((g) => g.items.length > 0);
  }, [navFilter]);

  useEffect(() => {
    if (navFilter.trim() === "") return;
    setOpenGroups((prev) => {
      const next = { ...prev };
      for (const g of filteredGroups) {
        next[g.id] = true;
      }
      return next;
    });
  }, [navFilter, filteredGroups]);

  const toggleGroup = (id: string) => {
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const closeMobile = () => setMobileOpen(false);

  const sidebarContent = (
    <>
      <div
        className={`p-6 border-b border-white/10 transition-all shrink-0 ${isCollapsed ? "px-4" : ""}`}
      >
        <SidebarBrandLogo isCollapsed={isCollapsed} />
      </div>

      {!isCollapsed && (
        <div
          className={`shrink-0 px-3 pt-3 pb-2 border-b border-white/10 ${
            theme === "dark" ? "bg-[var(--p-gray-950)]/80 backdrop-blur-md" : "bg-white/70 backdrop-blur-md"
          }`}
        >
          <div className="relative">
            <Search
              size={16}
              className={`absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none ${
                theme === "dark" ? "text-muted-foreground" : "text-muted-foreground"
              }`}
            />
            <input
              type="search"
              value={navFilter}
              onChange={(e) => setNavFilter(e.target.value)}
              placeholder="Filter navigation…"
              aria-label="Filter sidebar navigation"
              className={`w-full rounded-xl pl-9 pr-[4.5rem] py-2 text-sm outline-none ring-1 transition-shadow ${
                theme === "dark"
                  ? "bg-white/5 text-white placeholder:text-muted-foreground ring-white/10 focus:ring-[var(--p-epic)]/50"
                  : "bg-muted text-muted-foreground placeholder:text-muted-foreground ring-border focus:ring-[var(--p-epic)]/40"
              }`}
            />
            <div
              className={`absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 ${
                theme === "dark" ? "text-muted-foreground" : "text-muted-foreground"
              }`}
              title="Command palette"
            >
              <span className="exp-caption uppercase tracking-wider hidden sm:inline opacity-70">
                Palette
              </span>
              <kbd
                className={`exp-caption px-1 py-0.5 rounded border font-sans ${
                  theme === "dark"
                    ? "border-white/15 bg-white/5 text-muted-foreground"
                    : "border-border bg-white text-muted-foreground"
                }`}
              >
                ⌘K
              </kbd>
            </div>
          </div>
        </div>
      )}

      <nav className="flex-1 min-h-0 overflow-y-auto p-3">
        {!isCollapsed && filteredOverviewVisible && (
          <SidebarNavRow
            item={sidebarOverviewItem}
            isActive={isNavActive(sidebarOverviewItem.to)}
            isCollapsed={false}
            theme={theme}
            onNavigate={closeMobile}
          />
        )}

        {isCollapsed ? (
          <>
            {itemMatchesFilter(sidebarOverviewItem, navFilter) && (
              <SidebarNavRow
                item={sidebarOverviewItem}
                isActive={isNavActive(sidebarOverviewItem.to)}
                isCollapsed
                theme={theme}
                onNavigate={closeMobile}
              />
            )}
            {sidebarNavGroups.flatMap((g) =>
              g.items
                .filter((item) => itemMatchesFilter(item, navFilter))
                .map((item) => (
                  <SidebarNavRow
                    key={item.to}
                    item={item}
                    isActive={isNavActive(item.to)}
                    isCollapsed
                    theme={theme}
                    onNavigate={closeMobile}
                  />
                )),
            )}
          </>
        ) : (
          filteredGroups.map((group) => (
            <div key={group.id} className="mb-1">
              <button
                type="button"
                onClick={() => toggleGroup(group.id)}
                className={`flex items-center gap-2 w-full text-left px-2 py-2 rounded-lg mb-1 transition-colors ${
                  theme === "dark"
                    ? "text-muted-foreground hover:text-muted-foreground hover:bg-white/[0.04]"
                    : "text-muted-foreground hover:text-muted-foreground hover:bg-muted"
                }`}
                aria-expanded={openGroups[group.id] ?? true}
              >
                <ChevronDown
                  size={14}
                  className={`shrink-0 transition-transform opacity-70 ${
                    openGroups[group.id] ?? true ? "rotate-0" : "-rotate-90"
                  }`}
                />
                <span className="exp-caption font-semibold uppercase tracking-[0.14em]">
                  {group.label}
                </span>
              </button>
              {(openGroups[group.id] ?? true) &&
                group.items.map((item) => (
                  <SidebarNavRow
                    key={item.to}
                    item={item}
                    isActive={isNavActive(item.to)}
                    isCollapsed={false}
                    theme={theme}
                    onNavigate={closeMobile}
                  />
                ))}
            </div>
          ))
        )}

        <SidebarProductPicker theme={theme} isCollapsed={isCollapsed} onPick={closeMobile} />
      </nav>

      <div className={`p-4 border-t border-white/10 space-y-2 shrink-0`}>
        <button
          type="button"
          onClick={toggleTheme}
          className={`flex items-center gap-3 w-full px-4 py-2.5 rounded-xl transition-all duration-200 ${
            theme === "dark"
              ? "text-muted-foreground hover:text-white hover:bg-white/5"
              : "text-muted-foreground hover:text-muted-foreground hover:bg-muted"
          } ${isCollapsed ? "justify-center" : ""}`}
        >
          {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          {!isCollapsed && (
            <span className="text-sm">{theme === "dark" ? "Light Mode" : "Dark Mode"}</span>
          )}
        </button>

        <button
          type="button"
          onClick={onToggleCollapse}
          className={`hidden lg:flex items-center gap-3 w-full px-4 py-2.5 rounded-xl transition-all duration-200 ${
            theme === "dark"
              ? "text-muted-foreground hover:text-white hover:bg-white/5"
              : "text-muted-foreground hover:text-muted-foreground hover:bg-muted"
          } ${isCollapsed ? "justify-center" : ""}`}
        >
          {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          {!isCollapsed && <span className="text-sm">Collapse</span>}
        </button>
      </div>
    </>
  );

  return (
    <>
      <button
        type="button"
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-xl glass-thick text-white"
        onClick={() => setMobileOpen(!mobileOpen)}
      >
        {mobileOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/60 z-40 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside
        className={`z-40 flex flex-col transition-all duration-300 rounded-2xl shrink-0 lg:mr-4 min-h-0 ${
          mobileOpen
            ? "fixed top-4 left-4 h-[calc(100vh-32px)] translate-x-0"
            : "fixed lg:sticky top-4 left-4 lg:left-0 lg:top-8 lg:h-[calc(100vh-64px)] h-[calc(100vh-32px)] -translate-x-[calc(100%+32px)] lg:translate-x-0"
        } ${
          theme === "dark"
            ? "glass-thick"
            : "glass-thick border border-white/20 shadow-xl"
        }`}
        style={{ width: mobileOpen ? "280px" : isCollapsed ? "80px" : "var(--exp-sidebar-width)" }}
      >
        {sidebarContent}
      </aside>
    </>
  );
}
